// The activity table (S5 of documentation/pr-plans/insights-s0-plan-and-seats.md): the one table every
// Insights activity hands its rows to, drawn by components/site-analytics/activity-table.blade.php from
// the contract ActivityTable::normalize() checked. The contract, and what an activity answers an
// action with, are in documentation/context/site-analytics-contracts.md (The activity table).
//
// Everything here runs in the browser over the rows the activity sent (at most 500): sorting, the
// filters, the search, the selection and paging. The component is wire:ignore'd, so the activity never
// repaints it; a new answer (its rows loaded, its window changed) arrives as a new wire:key, which
// mounts a fresh table. An action is the one thing that leaves the browser: a cancelable
// `activity-table-action` event first, so the activity can answer in the browser (open a dialog,
// follow a link), else $wire.activityTableAction(table, action, ids), whose answer this applies.
// Two more things reach the activity: the topic Move to topic picked, as that call's fourth argument
// (P5, D101), and the first time a row is expanded, $wire.activityTableExpand(table, id), whose answer
// is the row's trend (D49). And one answer reaches the table that no press here asked for: a row the
// drawer's copy folded, or brought back (receiveAnswer(), S45.2).
//
// Its parts live in insights-activity-table/, one file per part, named for the task that owns it (the
// Blade parts are in components/site-analytics/activity-table/): toolbar.js and bulk-bar.js (S37),
// row.js (S36), detail.js (S36.1), slim-row.js (S37.1), row-actions.js (P5: the one button, the ⋯ and
// Move to topic), above-you.js (S53's Above you menu), pager.js (Ola's QA walk, 9 Oct: the pages) and
// groups.js (P10: Win back clicks' reasons). Each is a
// function of what it takes from this file (`shared`) that returns methods, which the table merges into
// the object it returns. This file keeps the state, init(), refresh(), callWire(), receiveAnswer(),
// afterShow() and the live regions.

import aboveYouPart from "./insights-activity-table/above-you.js";
import bulkBarPart from "./insights-activity-table/bulk-bar.js";
import detailPart from "./insights-activity-table/detail.js";
import groupsPart from "./insights-activity-table/groups.js";
import pagerPart from "./insights-activity-table/pager.js";
import rowPart from "./insights-activity-table/row.js";
import rowActionsPart from "./insights-activity-table/row-actions.js";
import slimRowPart from "./insights-activity-table/slim-row.js";
import toolbarPart from "./insights-activity-table/toolbar.js";
import whyPart from "./insights-activity-table/why.js";
import { currentBest, intentWeight } from "./insights-activity-table/intents.js";

// Funnel order, ready to buy first (D46); FunnelStage's cases in their declared order.
const STAGE_RANK = { bottom: 0, middle: 1, top: 2, not_relevant: 3 };

const ACTION_EVENT = "activity-table-action";

// The Livewire method an activity answers actions with (ActivityTable::HOST_METHOD).
const HOST_METHOD = "activityTableAction";

// The Livewire method an activity answers a row's first expand with (ActivityTable::EXPAND_METHOD).
const EXPAND_METHOD = "activityTableExpand";

// A row's one button (ActivityTable::DRAFT_ACTION); every other action waits in its ⋯ (P5, D98).
const DRAFT_ACTION = "draft";

// Actions a table may still send that no row draws (ActivityTable::MENU_HIDDEN_ACTIONS): Change stage
// (D101).
const MENU_HIDDEN_ACTIONS = ["stage"];

// Move to topic (OpportunityActivity::MOVE_ACTION): a choice, so its topic travels with the action.
const MOVE_ACTION = "move";

// The topic a pick of the built-in Not relevant topic sends (MoveTopicChoices::BUILT_IN), which has no id until a
// search is first set aside.
const BUILT_IN_TOPIC = "not_relevant";

// A row trend's box, in SVG units: each line is drawn in it on its own scale.
const SPARK_WIDTH = 160;
const SPARK_HEIGHT = 40;
const SPARK_PAD = 3;

// What Rows per page offers (pager.js), the first by default: 10 rows a page, so the next task is in
// sight with the few after it and the list never needs a scroll box of its own (P5, D98), and never
// more than 50, so a 500-row answer never puts 500 rows of controls in the page at once.
export const PAGE_SIZES = [10, 25, 50];

// Long enough that a reader typing a word hears one count, not one per letter.
const TYPING_ANNOUNCE_MS = 500;

// How long a row a link brought forward stays highlighted (D45's &row=): long enough to find on a long
// list, short enough not to read as a state of the row.
export const HIGHLIGHT_MS = 2500;

const collator = new Intl.Collator(undefined, { sensitivity: "base", numeric: true });

// A position as Google's pages count it, by BigQuery's ROUND (half away from zero, D12). Positions
// are never below 1, so that is Math.round.
export function roundedPosition(value) {
    return Math.round(value);
}

// What a search compares: case, accents and the space around the words do not count.
export function foldText(text) {
    return String(text).normalize("NFD").replace(/[̀-ͯ]/g, "").toLocaleLowerCase().trim();
}

function sortValue(row, column) {
    const cell = row.cells[column.key];

    if (!cell || cell.value === null || cell.value === undefined) {
        return null;
    }

    return column.kind === "stage" ? STAGE_RANK[cell.value] ?? null : cell.value;
}

// Two rows by one column. Unknown values sort last in either direction, so a press never brings the
// rows with nothing to compare to the top; ties keep the order the activity sent (`order`, id ->
// index), so a sort is stable and a list ranked by the activity stays ranked within a tie. A column
// that ranks buyers first (the Opportunity score, D130) compares the rows' search intents first, the
// most ready to buy first, as OpportunityScorer::buyersFirst() ranks the list.
export function compareRows(a, b, column, direction, order) {
    const left = sortValue(a, column);
    const right = sortValue(b, column);

    if (left === null || right === null) {
        if (left === right) {
            return order[a.id] - order[b.id];
        }

        return left === null ? 1 : -1;
    }

    const byIntent = column.ranksBuyersFirst === true ? intentWeight(a) - intentWeight(b) : 0;
    const result = byIntent !== 0 ? byIntent : column.kind === "text" ? collator.compare(String(left), String(right)) : left - right;

    if (result === 0) {
        return order[a.id] - order[b.id];
    }

    return direction === "desc" ? -result : result;
}

// Whether a Livewire request's JSON body carries one component whose every call passes `isCall`: the only
// request whose failure a table may keep Livewire's error dialog from (callWire()).
export function carriesOnly(payload, isCall) {
    let components;

    try {
        components = JSON.parse(payload).components;
    } catch {
        return false;
    }

    if (!Array.isArray(components) || components.length !== 1) {
        return false;
    }

    const calls = components[0].calls;

    return Array.isArray(calls) && calls.length > 0 && calls.every(isCall);
}

// One line of a row's trend as an SVG path in a SPARK_WIDTH by SPARK_HEIGHT box: each value at its
// label's place, scaled between the line's own lowest and highest value, and upside down when a lower
// value is the better one, so a position climbing towards 1 draws as a line going up. A gap in the
// figures breaks the line rather than drawing across it, and a lone figure is a dot (a zero-length
// stroke with round caps).
export function trendPath(values, isLowerBetter) {
    const known = values.filter((value) => typeof value === "number");

    if (known.length === 0) {
        return "";
    }

    const min = Math.min(...known);
    const span = Math.max(...known) - min;
    const step = values.length > 1 ? (SPARK_WIDTH - 2 * SPARK_PAD) / (values.length - 1) : 0;
    const height = SPARK_HEIGHT - 2 * SPARK_PAD;
    const round = (number) => Math.round(number * 100) / 100;
    const segments = [];
    let segment = [];

    values.forEach((value, index) => {
        if (typeof value !== "number") {
            segments.push(segment);
            segment = [];

            return;
        }

        const share = span === 0 ? 0.5 : (value - min) / span;
        const x = values.length > 1 ? SPARK_PAD + index * step : SPARK_WIDTH / 2;
        const y = SPARK_PAD + (isLowerBetter ? share : 1 - share) * height;

        segment.push(`${round(x)} ${round(y)}`);
    });
    segments.push(segment);

    return segments
        .filter((points) => points.length > 0)
        .map((points) => (points.length === 1 ? `M${points[0]} h0` : `M${points.join(" L")}`))
        .join(" ");
}

// What the parts take from this file: the names the contract shares with PHP (ActivityTableTest reads
// them here), and the limits and helpers this file or more than one part uses.
const shared = { ACTION_EVENT, HOST_METHOD, EXPAND_METHOD, DRAFT_ACTION, MENU_HIDDEN_ACTIONS, MOVE_ACTION, BUILT_IN_TOPIC, PAGE_SIZES, TYPING_ANNOUNCE_MS, HIGHLIGHT_MS, roundedPosition, foldText, trendPath };

export default function InsightsActivityTable({ table, strings, rowActions = [], presets = [], positionLimit = 100 }) {
    return {
        table,
        strings,
        // Every key ActivityTable::ROW_ACTIONS knows, in the order every row shows its buttons.
        rowActionOrder: rowActions,
        presets: presets.map(([from, to]) => ({ key: `${from}-${to}`, from, to })),
        positionLimit,

        rows: table.rows.slice(),
        // id -> the index the activity sent the row at: ties sort by it, and with no sort the rows keep it.
        order: {},

        sortKey: null,
        sortDirection: null,

        // null for any position, a preset's key, or "custom".
        positionPreset: null,
        positionFrom: null,
        positionTo: null,
        customFrom: "",
        customTo: "",
        isCustomInvalid: false,
        stagesPicked: [],
        topicsPicked: [],
        topicQuery: "",
        query: "",

        // id -> true. Always a subset of the matched rows: a filter drops what it hides.
        selected: {},
        // id -> {text, undo, kind}: a finished row folded into its notice, or on a card table its slim row
        // (D53, D54, D70); kind is done or not_relevant.
        notices: {},
        // id -> {kind, undo}: the fold of a row whose notice the reader closed, which took the row away but
        // undid nothing, so the activity's summary still counts it done (ROWS_EVENT, closeNotice()).
        closedFolds: {},
        // id -> true while the activity answers an action on the row.
        pending: {},
        isBulkPending: false,
        // id -> true for an unsafe search the reader asked to read (D47).
        revealed: {},
        // id -> true for a row whose detail is open (D49).
        expanded: {},
        // id -> {state: loading, ready or error, trend, message}: a row's trend, asked for the first
        // time the row opens and kept while the table lives.
        trends: {},
        // The id of the row whose ⋯ is open, or null: one at a time (row-actions.js), and how many times
        // one has opened, which keys its panel so each opening is placed against its own ⋯.
        menuFor: null,
        menuOpenings: 0,
        // Move to topic's dialog while it is open: {ids, origin, current}, `current` the key of the topic one
        // row's search is in now (row-actions.js), and the topic picked in it.
        moveDialog: null,
        movePick: null,

        // Above you's menu (above-you.js): the id of the row whose menu is open, or null; the site's reads
        // this week as last answered; whether a press is out, and {id, state, nextRead} for its answer; when
        // a read was last asked for (ms); and id -> when each row's Above you was answered (ms).
        aboveMenuFor: null,
        aboveChecks: null,
        isAboveCheckPending: false,
        aboveCheckAnswer: null,
        aboveCheckedAt: null,
        aboveAnsweredAt: {},

        // The page of `matched` the table draws, from 1 (pager.js). Not `page`: a target's edit form
        // (activity-table/target-detail) holds a `page` of its own, and a table method called from inside
        // that form reads the form's scope first.
        currentPage: 1,
        // How many rows a page holds: one of PAGE_SIZES, the reader's Rows per page (pager.js).
        pageSize: PAGE_SIZES[0],
        openPanel: null,

        // The rows the filters keep, sorted, and the page of them the table draws.
        matched: [],
        visibleRows: [],
        // The id of the list's current best search (D130), worked out once per change in refresh(): read by every
        // binding of every row's Why it ranks, it was a scan of the whole list per binding, so a fold re-ran
        // thousands of scans and froze the page for half a second (P83, 22 Oct).
        currentBestId: null,

        announcement: "",
        alertText: "",
        error: "",
        announceTimer: null,

        // The id of the row a link brought forward, while it is highlighted (bringForward()).
        highlightedRow: null,
        highlightTimer: null,

        // The table's own element, kept from init. $root is not: inside a method it is worked out from
        // the element whose press called it, and a notice's Undo is gone from the page by the time its
        // answer moves focus, so $root there is undefined.
        rootElement: null,

        init() {
            this.rootElement = this.$root;
            this.rows.forEach((row, index) => {
                this.order[row.id] = index;
            });
            this.seedFolded();

            if (this.table.defaultSort) {
                this.sortKey = this.table.defaultSort.column;
                this.sortDirection = this.table.defaultSort.direction;
            }

            this.refresh();
            this.bringForward(this.table.openRow);
            this.openFirstRow();
        },

        // A table remounted by a new answer must not speak for the one it replaced.
        destroy() {
            clearTimeout(this.announceTimer);
            clearTimeout(this.highlightTimer);
        },

        // ------------------------------------------------------------------------- the view itself

        refresh() {
            let rows = this.rows.filter((row) => this.isMatched(row));

            if (this.sortKey !== null) {
                const column = this.columnOf(this.sortKey);

                rows = rows.slice().sort((a, b) => compareRows(a, b, column, this.sortDirection, this.order));
            }

            this.matched = this.groupRows(rows);
            // The reader's page stays, unless rows leaving emptied it: then the last page left.
            this.currentPage = Math.min(Math.max(1, this.currentPage), this.pageCount());
            this.visibleRows = this.rowsOnPage(this.currentPage);
            this.pruneSelection();
            // Every change to the rows or their folds ends here, so the current best search is worked out here.
            this.currentBestId = currentBest(this.rows, (candidate) => !this.noticeOf(candidate.id))?.id ?? null;
        },

        // Convention exception — settling a Livewire call on failure:
        // Livewire's $wire promise (3.6.4 and 3.8.6 alike) resolves on success only; a request that fails (a
        // 5xx, the network gone) runs Commit::handleFailure() and never settles it, so the row would wait
        // forever. The fix belongs in Livewire (vendor/livewire/livewire/dist/livewire.esm.js,
        // Commit.addCall); until then the call races its own commit's fail hook, matched by method and
        // arguments and unhooked either way. The table then says the call failed itself (the alert, "That
        // did not save"), so Livewire's own error dialog would only repeat it over the whole page: the
        // request this commit travels in is told not to open it, when this call is all it carries and it
        // is not a page expiry (419), which Livewire must still offer to reload.
        callWire(method, params) {
            const livewire = typeof window !== "undefined" ? window.Livewire : null;
            const signature = JSON.stringify(params);
            const isThisCall = (call) => call.method === method && JSON.stringify(call.params) === signature;

            return new Promise((resolve, reject) => {
                let unhook = () => {};
                let unhookRequest = () => {};
                // Every way the call ends removes both hooks, so neither outlives it.
                const unhookAll = () => {
                    unhook();
                    unhookRequest();
                };

                if (livewire && typeof livewire.hook === "function") {
                    unhook = livewire.hook("commit", ({ commit, fail }) => {
                        if ((commit.calls || []).some(isThisCall)) {
                            unhook();
                            fail(() => {
                                unhookAll();
                                reject(new Error("The request failed."));
                            });
                            // Livewire triggers the request a commit travels in next, in the same tick.
                            unhookRequest = livewire.hook("request", ({ payload, fail: failRequest }) => {
                                unhookRequest();

                                if (carriesOnly(payload, isThisCall)) {
                                    failRequest(({ status, preventDefault }) => {
                                        if (status !== 419) {
                                            preventDefault();
                                        }
                                    });
                                }
                            });
                        }
                    });
                }

                this.$wire[method](...params).then(
                    (value) => {
                        unhookAll();
                        resolve(value);
                    },
                    (error) => {
                        unhookAll();
                        reject(error);
                    },
                );
            });
        },

        // An answer the activity sends with no press here asking for it (ActivityTable::ANSWER_EVENT,
        // `{table, answer}` on the window): the row a copy in the drawer folded, or the row the drawer's
        // Undo brought back (S45.2). Every table on the page hears it, so only the one it names applies
        // it, in place and in silence: the drawer the change came from says what happened, and holds
        // focus behind its trap.
        receiveAnswer(detail) {
            if (!detail || detail.table !== this.table.id || !detail.answer || typeof detail.answer !== "object") {
                return;
            }

            this.apply(detail.answer, { kind: "elsewhere" });
        },

        // x-show shows an element a frame late (Alpine's click-away-safe show, on requestAnimationFrame,
        // or setTimeout in a hidden tab) while it hides one at once. A focus move to something just
        // shown (a notice's Undo, a restored row, an opened panel) waits for that frame, or it lands on
        // an element still display:none, which takes no focus, and the reader is left on the page.
        afterShow(callback) {
            this.$nextTick(() => {
                const later = typeof document !== "undefined" && document.visibilityState === "visible" ? requestAnimationFrame : setTimeout;

                later(() => callback());
            });
        },

        // ------------------------------------------------------------------------- live regions

        // Cleared first, so the same words said twice are heard twice; a delay folds typing into one.
        announce(text, delay = 0) {
            clearTimeout(this.announceTimer);

            const say = () => {
                this.announcement = "";
                this.$nextTick(() => {
                    this.announcement = text;
                });
            };

            if (delay > 0) {
                this.announceTimer = setTimeout(say, delay);
            } else {
                say();
            }
        },

        alert(text) {
            this.alertText = "";
            this.$nextTick(() => {
                this.alertText = text;
            });
        },

        // ------------------------------------------------------------------------------- the parts

        ...toolbarPart(shared),
        ...pagerPart(shared),
        ...bulkBarPart(shared),
        ...rowPart(shared),
        ...rowActionsPart(shared),
        ...detailPart(shared),
        ...slimRowPart(shared),
        ...aboveYouPart(shared),
        ...groupsPart(shared),
        ...whyPart(shared),
    };
}
